import { useGetInvoicesQuery, useUpdateInvoiceMutation } from "api/TenantSlug/Invoice/hooks";
import { TableColumn } from "components/UI/Table/columns/TableColumn.tsx";
import { MoneyCell } from "components/UI/Table/UI/MoneyCell/MoneyCell";
import dayjs from "dayjs";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { InvoiceStatus } from "types/enums";

import { ApproveRejectButtons } from "../shared/ActionChips";
import { useOptimisticHide } from "../shared/hooks/useOptimisticHide";
import { SearchableTable } from "../shared/SearchableTable";
import { StateTag } from "../shared/StateTag";
import { ApiErr, UpdateInvoiceVariables } from "../shared/types";
import { renderDateSmart, syncDayjsLocale } from "../shared/utils/dateLocale";
import { fmtDueState } from "../shared/utils/fmtDueState";

import InvoiceData = App.Data.InvoiceData;

export const AdvertiserPayoutRequests: React.FC = () => {
    const { t, i18n } = usePaldockTranslation("ui.dashboard.cards.advertiser-payout-requests");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tC } = usePaldockTranslation("common");

    useEffect(() => {
        syncDayjsLocale(i18n.language);
    }, [i18n.language]);

    const query = useGetInvoicesQuery(
        {
            page: {
                number: 1,
                size: 5,
            },
            filter: {
                filter: { status: "pending" },
                sort: "-created_at",
                include: "to,from",
            },
        },
        {
            staleTime: 5000, // 5 seconds
            refetchInterval: 10000, // Refetch every 5 seconds
        }
    );

    const { hide, unhide, filterVisible } = useOptimisticHide();

    const updateInvoiceMutation = useUpdateInvoiceMutation({
        onSuccess: async () => {
            await query.refetch();
            toast.success(t("messages.update_success"));
        },
        onError: (_err: ApiErr, variables: UpdateInvoiceVariables) => {
            unhide(variables.invoiceId);
            toast.error(t("messages.update_error"));
        },
    });

    const updateStatus = (id: string, status: InvoiceStatus) => {
        hide(id);
        updateInvoiceMutation.mutate({ invoiceId: id, request: { status } });
    };

    const rows = useMemo(
        () => filterVisible<InvoiceData>(query.data?.invoices, "pending"),
        [query.data?.invoices, filterVisible]
    );

    return (
        <SearchableTable<InvoiceData>
            rows={rows}
            loading={query.isLoading || query.isRefetching}
            rowKey={(r: InvoiceData) => r.id}
            allowSearch={false}
            searchKeys={[
                (r: InvoiceData) => (r.created_at ? dayjs(r.created_at).format("D.M.YYYY HH:mm") : ""),
                (r: InvoiceData) => (r.created_at ? dayjs(r.created_at).format("D.M.YYYY") : ""),
                (r: InvoiceData) => r?.to?.nickname ?? "",
                (r: InvoiceData) => r.formatted_amount ?? r.amount?.toString() ?? "",
                (r: InvoiceData) => fmtDueState(r, t).label,
            ]}
            columns={[
                {
                    title: tG("date"),
                    dataIndex: "created_at",
                    render: (v: string) => renderDateSmart(v),
                },
                TableColumn.Entity.Advertiser<InvoiceData>({
                    title: tG("advertiser"),
                    dataIndex: ["to", "nickname"],
                    key: "to.nickname",
                    sorter: false,
                    filterable: false,
                    getEntity: (data) => data.to,
                }),
                {
                    title: t("columns.amount"),
                    dataIndex: "formatted_amount",
                    render: (_: unknown, r: InvoiceData) => (
                        <MoneyCell
                            money={{
                                amount: r.amount,
                                currency_id: r.currency_id,
                                formatted_amount: r.formatted_amount,
                            }}
                        />
                    ),
                },
                {
                    title: tG("state"),
                    key: "state",
                    render: (_: unknown, r: InvoiceData) => {
                        const { label, tone } = fmtDueState(r, t);
                        return <StateTag v={label} tone={tone} />;
                    },
                },
                {
                    title: tG("actions"),
                    key: "actions",
                    align: "center",
                    width: 120,
                    render: (_: unknown, r: InvoiceData) => (
                        <ApproveRejectButtons
                            state={r.status}
                            pendingValue="pending"
                            approvedValue="approved"
                            rejectedValue="rejected"
                            labels={{ approve: tC("approve"), reject: tC("reject") }}
                            loading={updateInvoiceMutation.isPending}
                            onApprove={() => updateStatus(r.id, InvoiceStatus.Approved)}
                            onReject={() => updateStatus(r.id, InvoiceStatus.Rejected)}
                        />
                    ),
                },
            ]}
        />
    );
};
